import React from "react";
import { useCMS } from "../../context/CMSContext";
import { useLang } from "../../context/LangContext";
import styles from "./PageRenderer.module.css";

// Auto-link URLs and emails inside plain text
function RichText({ text }) {
  if (!text) return null;

  const pattern =
    /(https?:\/\/[^\s<>"']+)|([a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,})/g;

  const segments = [];
  let last = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) {
      segments.push({ type: "text", value: text.slice(last, match.index) });
    }
    segments.push({
      type: match[1] ? "url" : "email",
      value: match[0],
    });
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    segments.push({ type: "text", value: text.slice(last) });
  }

  return (
    <>
      {segments.map((seg, i) => {
        if (seg.type === "url") {
          return (
            <a
              key={i}
              href={seg.value}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.link}
            >
              {seg.value}
            </a>
          );
        }
        if (seg.type === "email") {
          return (
            <a key={i} href={`mailto:${seg.value}`} className={styles.link}>
              {seg.value}
            </a>
          );
        }
        // Preserve line breaks
        return seg.value.split("\n").map((line, j, arr) => (
          <React.Fragment key={`${i}-${j}`}>
            {line}
            {j < arr.length - 1 && <br />}
          </React.Fragment>
        ));
      })}
    </>
  );
}

export function PageRenderer({ pageKey }) {
  const { cms, loading } = useCMS();
  const { lang = "en" } = useLang();
  const page = cms?.[pageKey];

  if (loading) {
    return (
      <div className={styles.loading}>
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className={styles.skeletonLine}
            style={{ width: `${70 + (i % 3) * 10}%` }}
          />
        ))}
      </div>
    );
  }

  if (!page || !page.sections?.length) {
    return <div className={styles.empty}>Content coming soon.</div>;
  }

  const updated = page.updatedAt?.toDate
    ? page.updatedAt
        .toDate()
        .toLocaleDateString(lang === "tr" ? "tr-TR" : "en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
    : null;

  return (
    <div className={styles.doc}>
      {updated && (
        <p className={styles.updated}>
          {lang === "tr" ? "Son güncelleme:" : "Last updated:"} {updated}
        </p>
      )}

      {page.sections.map((section) => {
        const heading = section[`heading_${lang}`] || section.heading_en || "";
        const content = section[`content_${lang}`] || section.content_en || "";

        return (
          <div
            key={section.id}
            className={styles[section.type] || styles.paragraph}
          >
            {section.type === "section" && heading && (
              <h2 className={styles.sectionH}>{heading}</h2>
            )}
            {section.type === "subsection" && heading && (
              <h3 className={styles.subsectionH}>{heading}</h3>
            )}
            {content && (
              <p className={styles.body}>
                <RichText text={content} />
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
